import { Vulnerability, RiskFactor } from '../types/vulnerability';

/**
 * Efficiently loads and processes large JSON vulnerability data files
 * Uses streaming/chunked approach for better memory management
 */
export class VulnerabilityDataLoader {
  private data: Vulnerability[] = [];
  private indexedData: Map<string, Vulnerability> = new Map();
  private loadingRequests: Map<string, Promise<Vulnerability[]>> = new Map();

  /**
   * Load vulnerability data from JSON file
   * Handles large files efficiently
   */
  async loadData(filePath: string): Promise<Vulnerability[]> {
    const existingRequest = this.loadingRequests.get(filePath);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async () => {
      const response = await fetch(filePath);

      if (!response.ok) {
        throw new Error(`Failed to load data: ${response.statusText}`);
      }

      const jsonData = await response.json();

      const rawVulnerabilities = this.extractVulnerabilities(jsonData);

      // Normalize and validate data
      this.data = this.normalizeData(rawVulnerabilities);
      this.indexData();

      return this.data;
    })();

    this.loadingRequests.set(filePath, request);

    try {
      return await request;
    } finally {
      this.loadingRequests.delete(filePath);
    }
  }

  /**
   * Normalize vulnerability data to ensure consistent structure
   */
  private normalizeData(data: unknown[]): Vulnerability[] {
    return data.filter(Boolean).map((raw, index) => {
      const vuln = raw as Record<string, unknown>;
      const cveId =
        this.extractString(vuln['cve']) || this.extractString(vuln['cveId']) || undefined;
      // Ensure each vulnerability has an ID
      const id = this.extractString(vuln.id) || cveId || `vuln-${index}`;

      const normalized: Vulnerability = {
        ...vuln,
        id,
        cveId,
        packageName:
          this.extractString(vuln['packageName']) ||
          this.extractString(vuln['component']) ||
          this.extractString(vuln['package']),
        packageVersion:
          this.extractString(vuln['packageVersion']) ||
          this.extractString(vuln['version']) ||
          this.extractString(vuln['packageVersionLabel']),
        severity: this.normalizeSeverity(vuln['severity']),
        kaiStatus: this.normalizeKaiStatus(vuln['kaiStatus'] ?? vuln['status']),
        riskFactors: this.normalizeRiskFactors(vuln['riskFactors']),
        score: this.extractScore(vuln['score'], vuln['cvss'], vuln['cvssScore']),
        description: this.extractString(vuln['description']) || this.extractString(vuln['summary']),
        publishedDate: this.normalizeDate(
          vuln['published'] ?? vuln['publishedDate'] ?? vuln['layerTime']
        ),
        modifiedDate: this.normalizeDate(
          vuln['modified'] ?? vuln['modifiedDate'] ?? vuln['fixDate']
        ),
        references: this.normalizeReferences(vuln),
      };

      if (vuln['groupName'] && !normalized.groupName) {
        normalized.groupName = vuln['groupName'];
      }
      if (vuln['repoName'] && !normalized.repoName) {
        normalized.repoName = vuln['repoName'];
      }
      if (vuln['imageName'] && !normalized.imageName) {
        normalized.imageName = vuln['imageName'];
      }
      if (vuln['imageVersion'] && !normalized.imageVersion) {
        normalized.imageVersion = vuln['imageVersion'];
      }

      return normalized;
    });
  }

  /**
   * Normalize severity values
   */
  private normalizeSeverity(severity?: string | unknown): Vulnerability['severity'] {
    if (!severity) return 'unknown';
    const s = String(severity).toLowerCase();
    if (['critical', 'high', 'medium', 'low', 'info'].includes(s)) {
      return s as Vulnerability['severity'];
    }
    return 'unknown';
  }

  /**
   * Normalize kaiStatus values
   */
  private normalizeKaiStatus(kaiStatus?: string | unknown): Vulnerability['kaiStatus'] {
    if (!kaiStatus) return 'unknown';
    const status = String(kaiStatus).toLowerCase().trim();

    // Handle various formats
    if (status.includes('invalid') && status.includes('norisk')) {
      if (status.includes('ai')) {
        return 'ai-invalid-norisk';
      }
      return 'invalid - norisk';
    }

    if (status.includes('valid')) {
      if (status.includes('ai')) {
        return 'ai-valid';
      }
      return 'valid';
    }

    if (status.includes('fixed')) {
      return 'valid';
    }

    if (status === 'affected' || status.startsWith('affected ') || status.endsWith(' affected')) {
      return 'pending';
    }

    if (status.includes('pending') || status.includes('awaiting')) {
      return 'pending';
    }

    if (status === 'pending') return 'pending';

    return 'unknown';
  }

  /**
   * Index data for faster lookups
   */
  private indexData(): void {
    this.indexedData.clear();
    this.data.forEach(vuln => {
      if (vuln.id) {
        this.indexedData.set(vuln.id, vuln);
      }
      if (vuln.cveId && vuln.cveId !== vuln.id) {
        this.indexedData.set(vuln.cveId, vuln);
      }
    });
  }

  /**
   * Get all vulnerabilities
   */
  getAll(): Vulnerability[] {
    return this.data;
  }

  /**
   * Get vulnerability by ID
   */
  getById(id: string): Vulnerability | undefined {
    return this.indexedData.get(id);
  }

  /**
   * Get total count
   */
  getCount(): number {
    return this.data.length;
  }

  /**
   * Generate mock data for development/testing fallbacks
   */
  generateMockData(count = 1000): Vulnerability[] {
    const severities: Vulnerability['severity'][] = ['critical', 'high', 'medium', 'low', 'info'];
    const kaiStatuses: Vulnerability['kaiStatus'][] = [
      'invalid - norisk',
      'ai-invalid-norisk',
      'valid',
      'ai-valid',
      'pending',
    ];

    const mockData: Vulnerability[] = Array.from({ length: count }, (_, index) => {
      const severity = severities[Math.floor(Math.random() * severities.length)];
      const kaiStatus = kaiStatuses[Math.floor(Math.random() * kaiStatuses.length)];

      return {
        id: `CVE-2024-${String(index).padStart(5, '0')}`,
        cveId: `CVE-2024-${String(index).padStart(5, '0')}`,
        packageName: `package-${Math.floor(Math.random() * 50)}`,
        packageVersion: `${Math.floor(Math.random() * 5) + 1}.${Math.floor(Math.random() * 10)}.${Math.floor(
          Math.random() * 20
        )}`,
        severity,
        kaiStatus,
        score: Number((Math.random() * 10).toFixed(1)),
        description: `Mock vulnerability description ${index}`,
        publishedDate: new Date(
          Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000
        ).toISOString(),
        riskFactors: [
          { name: 'vector', value: Math.random() > 0.5 ? 'network' : 'local' },
          { name: 'attack_complexity', value: Math.random() > 0.5 ? 'low' : 'high' },
        ],
      } as Vulnerability;
    });

    const normalized = this.normalizeData(mockData);
    this.data = normalized;
    this.indexData();

    return normalized;
  }

  private extractVulnerabilities(jsonData: unknown): unknown[] {
    if (Array.isArray(jsonData)) {
      return jsonData;
    }

    if (!jsonData || typeof jsonData !== 'object') {
      return [];
    }

    const record = jsonData as Record<string, unknown>;

    if (Array.isArray(record.vulnerabilities)) {
      return record.vulnerabilities;
    }

    if (Array.isArray(record.data)) {
      return record.data;
    }

    if (record.groups && typeof record.groups === 'object') {
      return this.flattenGroupStructure(record.groups as Record<string, unknown>);
    }

    return Object.values(record).flatMap(value => this.extractVulnerabilities(value));
  }

  private flattenGroupStructure(groups: Record<string, unknown>): unknown[] {
    const results: unknown[] = [];

    for (const [groupName, groupValue] of Object.entries(groups)) {
      if (!groupValue || typeof groupValue !== 'object') continue;
      const group = groupValue as Record<string, unknown>;
      const repos = group['repos'] as Record<string, unknown> | undefined;
      if (!repos) continue;

      for (const [repoName, repoValue] of Object.entries(repos)) {
        if (!repoValue || typeof repoValue !== 'object') continue;
        const repo = repoValue as Record<string, unknown>;
        const images = repo['images'] as Record<string, unknown> | undefined;
        if (!images) continue;

        for (const [imageKey, imageValue] of Object.entries(images)) {
          if (!imageValue || typeof imageValue !== 'object') continue;
          const image = imageValue as Record<string, unknown>;
          const vulnerabilities = Array.isArray(image['vulnerabilities'])
            ? image['vulnerabilities']
            : [];
          const imageName = this.extractString(image['name']) || imageKey;
          const imageVersion = this.extractString(image['version']) || imageKey;

          vulnerabilities.forEach((vuln, index) => {
            results.push({
              ...vuln,
              groupName,
              repoName,
              imageName,
              imageVersion,
              baseImage: image['baseImage'],
              buildType: image['buildType'],
              maintainer: image['maintainer'],
              imageMetadata: {
                groupName,
                repoName,
                imageName,
                imageVersion,
                baseImage: image['baseImage'],
                buildType: image['buildType'],
                maintainer: image['maintainer'],
              },
              sourceIndex: index,
            });
          });
        }
      }
    }

    return results;
  }

  private extractString(value: unknown): string | undefined {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    }
    return undefined;
  }

  private normalizeRiskFactors(input: unknown): RiskFactor[] {
    if (!input) return [];

    if (Array.isArray(input)) {
      return input
        .map(item => {
          if (!item || typeof item !== 'object') return undefined;
          const factor = item as RiskFactor;
          if (!factor.name) return undefined;
          return factor;
        })
        .filter((item): item is RiskFactor => Boolean(item));
    }

    if (typeof input === 'object') {
      return Object.entries(input as Record<string, unknown>)
        .map(([rawName, rawValue]) => {
          const { name, value } = this.splitRiskFactorName(rawName);
          let category: string | undefined;
          let factorValue: string | number = value;

          if (rawValue && typeof rawValue === 'object') {
            const valueRecord = rawValue as Record<string, unknown>;
            if (valueRecord.value !== undefined) {
              factorValue = valueRecord.value as string | number;
            }
            if (valueRecord.category && typeof valueRecord.category === 'string') {
              category = valueRecord.category;
            }
          }

          const factor: RiskFactor = {
            name,
            value: factorValue === '' ? 1 : factorValue,
            ...(category ? { category } : {}),
          };

          return factor;
        })
        .filter(item => Boolean(item.name));
    }

    return [];
  }

  private splitRiskFactorName(name: string): { name: string; value: string } {
    if (!name.includes(':')) {
      return { name: name.trim(), value: '' };
    }

    const [factorName, ...rest] = name.split(':');
    return {
      name: factorName.trim(),
      value: rest.join(':').trim(),
    };
  }

  private extractScore(...values: unknown[]): number | undefined {
    for (const value of values) {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      if (value && typeof value === 'object' && 'baseScore' in (value as Record<string, unknown>)) {
        const baseScore = (value as Record<string, unknown>).baseScore;
        if (typeof baseScore === 'number' && !Number.isNaN(baseScore)) {
          return baseScore;
        }
      }
    }
    return undefined;
  }

  private normalizeDate(value: unknown): string | undefined {
    if (!value) return undefined;
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return undefined;

      const direct = new Date(trimmed);
      if (!Number.isNaN(direct.getTime())) {
        return direct.toISOString();
      }

      const normalized = trimmed.replace(' ', 'T');
      const isoCandidate = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
      const parsed = new Date(isoCandidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString();
      }
    }
    return undefined;
  }

  private normalizeReferences(vuln: Record<string, unknown>): string[] | undefined {
    const references = new Set<string>();

    const consider = (value: unknown) => {
      if (typeof value === 'string' && value.trim().length) {
        references.add(value.trim());
      }
      if (Array.isArray(value)) {
        value.forEach(entry => consider(entry));
      }
    };

    consider(vuln['references']);
    consider(vuln['links']);
    consider(vuln['link']);

    if (references.size === 0) {
      return undefined;
    }

    return Array.from(references);
  }
}

// Singleton instance
export const dataLoader = new VulnerabilityDataLoader();
